import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { EnvelopeSimple, LockKey } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Enter email, 2: Enter OTP & new password
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      toast.success('Reset code sent to your email');
      
      // Show OTP in dev mode
      if (response.data.otp) {
        toast.info(`Dev Mode - Reset Code: ${response.data.otp}`, { duration: 10000 });
      }
      
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset code');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!otp || !newPassword || !confirmPassword) {
      toast.error('Please fill all fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        email,
        otp,
        new_password: newPassword
      });
      
      toast.success('Password reset successful! You can now login.');
      navigate('/auth');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="backdrop-blur-xl bg-[#121212] border border-white/10 p-8">
          <div className="mb-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center">
              <LockKey size={32} className="text-[#D4AF37]" weight="duotone" />
            </div>
            <h1 className="text-3xl font-serif text-white mb-2">
              {step === 1 ? 'Forgot Password?' : 'Reset Password'}
            </h1>
            <p className="text-zinc-400">
              {step === 1 
                ? 'Enter your email to receive a reset code' 
                : 'Enter the code and your new password'}
            </p>
          </div>

          {step === 1 ? (
            <form onSubmit={handleSendOTP} className="space-y-6">
              <div>
                <Label htmlFor="email" className="text-white mb-2 block">
                  Email Address
                </Label>
                <div className="flex items-center gap-2 bg-transparent border border-white/20 px-4">
                  <EnvelopeSimple size={20} className="text-zinc-500" weight="duotone" />
                  <Input
                    data-testid="forgot-password-email-input"
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-transparent border-none text-white"
                    placeholder="your.email@example.com"
                  />
                </div>
              </div>

              <Button
                data-testid="send-reset-code-button"
                type="submit"
                disabled={loading}
                className="w-full bg-[#D4AF37] hover:bg-[#B5952F] text-black font-semibold py-3"
              >
                {loading ? 'Sending...' : 'Send Reset Code'}
              </Button>

              <div className="text-center">
                <Link
                  to="/auth"
                  className="text-zinc-400 hover:text-[#D4AF37] transition-colors text-sm"
                >
                  ← Back to Login
                </Link>
              </div>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-6">
              <div>
                <Label htmlFor="otp" className="text-white mb-2 block">
                  Reset Code
                </Label>
                <Input
                  data-testid="reset-otp-input"
                  id="otp"
                  type="text"
                  required
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  className="bg-transparent border-white/20 text-white"
                  placeholder="Enter 6-digit code"
                  maxLength={6}
                />
              </div>

              <div>
                <Label htmlFor="newPassword" className="text-white mb-2 block">
                  New Password
                </Label>
                <Input
                  data-testid="new-password-input"
                  id="newPassword"
                  type="password"
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="bg-transparent border-white/20 text-white"
                  placeholder="Enter new password"
                />
              </div>

              <div>
                <Label htmlFor="confirmPassword" className="text-white mb-2 block">
                  Confirm Password
                </Label>
                <Input
                  data-testid="confirm-password-input"
                  id="confirmPassword"
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="bg-transparent border-white/20 text-white"
                  placeholder="Confirm new password"
                />
              </div>

              <Button
                data-testid="reset-password-button"
                type="submit"
                disabled={loading}
                className="w-full bg-[#D4AF37] hover:bg-[#B5952F] text-black font-semibold py-3"
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>

              <div className="text-center space-y-2">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="text-zinc-400 hover:text-[#D4AF37] transition-colors text-sm block w-full"
                >
                  ← Use different email
                </button>
                <Link
                  to="/auth"
                  className="text-zinc-400 hover:text-[#D4AF37] transition-colors text-sm block"
                >
                  Back to Login
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}